import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useOutletContext } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import PaginatedSortableTable from "../components/PaginatedSortableTable";
import DateTimeRangePicker from "../components/DateTimeRangePicker";
import {
  getInitialDateTimeRange,
  getPresetDateTimeRange,
} from "../utils/dateRange";
dayjs.extend(utc);
dayjs.extend(timezone);

export default function LimitChangeLogs() {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const { setIsLoggedIn } = useOutletContext();
  const navigate = useNavigate();
  const initialRange = getInitialDateTimeRange();
  const [startDateTime, setStartDateTime] = useState(initialRange.start);
  const [endDateTime, setEndDateTime] = useState(initialRange.end);
  const [users, setUsers] = useState(null);
  const [logsData, setLogsData] = useState(null);
  const [totalLogs, setToTalLogs] = useState(null);
  const [vendors, setVendors] = useState(null);
  const [GameList, setGameList] = useState(null);

  const [searchKeyword, setSearchKeyword] = useState("");

  const handleDateRange = (type) => {
    const range = getPresetDateTimeRange(type);
    setStartDateTime(range.start);
    setEndDateTime(range.end);
  };

  const vendorMap = React.useMemo(() => {
    if (!vendors) return {};
    return vendors.reduce((acc, v) => {
      acc[v.v_code] = v.v_name_kor;
      return acc;
    }, {});
  }, [vendors]);

  const sortOptions = [
    { value: "time_request:asc", label: "요청시간 - 내림차순" },
    { value: "time_request:desc", label: "요청시간 - 오름차순" },
  ];

  
  const getGameList = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE}/api/GameList`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setGameList(res.data.vendor_list);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        if (
          err.response.status === 401 ||
          err.response.status === 403 ||
          err.response.status === 500
        ) {
          localStorage.removeItem("token");
          setIsLoggedIn(false); // 로그인 상태 false 처리
          navigate("/login"); // 로그인 화면으로 리다이렉트
          return;
        }
      }
      console.error("조회 실패:", err);
    }
  };

  const getVendors = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE}/api/Vendors`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setVendors(res.data.vendor_list);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        if (
          err.response.status === 401 ||
          err.response.status === 403 ||
          err.response.status === 500
        ) {
          localStorage.removeItem("token");
          setIsLoggedIn(false); // 로그인 상태 false 처리
          navigate("/login"); // 로그인 화면으로 리다이렉트
          return;
        }
      }
      console.error("조회 실패:", err);
    }
  };

  useEffect(() => {
    getGameList();
    getVendors();
  }, []);

  useEffect(() => {
    if (!logsData) return;

    const filtered = logsData.filter((log) => {
      if (!searchKeyword) return true;

      const value = String(log.user_id || ""); // ✅ 무조건 문자열

      return value.includes(searchKeyword);
    });

    setToTalLogs(filtered);
  }, [logsData, searchKeyword]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_BASE}/api/Users`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setUsers(res.data);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response) {
          if (
            err.response.status === 401 ||
            err.response.status === 403 ||
            err.response.status === 500
          ) {
            localStorage.removeItem("token");
            setIsLoggedIn(false); // 로그인 상태 false 처리
            navigate("/login"); // 로그인 화면으로 리다이렉트
            return;
          }
        }
        console.error("유저 정보 불러오기 실패:", err);
      }
    };

    fetchUserInfo();
  }, []);

  const fetchLogsData = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE}/api/LimitChangeLogs`, {
        params: {
          processed_at_start: startDateTime.replace("T", " ") + ":00",
          processed_at_end: endDateTime.replace("T", " ") + ":59",
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setLogsData(res.data);
      setToTalLogs(res.data);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        if (
          err.response.status === 401 ||
          err.response.status === 403 ||
          err.response.status === 500
        ) {
          localStorage.removeItem("token");
          setIsLoggedIn(false); // 로그인 상태 false 처리
          navigate("/login"); // 로그인 화면으로 리다이렉트
          return;
        }
      }
      console.error("조회 실패:", err);
    }
  };

  const getGameName = (log, gameList) => {
  if (!log || !gameList || gameList.length === 0) return log?.target_id || "-";

  const game = gameList.find(
    (g) =>
      String(g.vcode).toLowerCase() === String(log.vendor).toLowerCase() &&
      String(g.game_id) === String(log.target_id)
  );

  if (!game) return log.target_id;

  // game_name_kor 우선, 없으면 game_name
  return game.game_name_kor && game.game_name_kor.trim() !== ""
    ? game.game_name_kor
    : game.game_name;
};


  const getAllUsers = () => {
    const list = [];
    if (!users) return list;

    // 1) 최상위 users.users 에 속한 유저들
    users.users.forEach((u) => list.push(u));

    // 2) 에이전트 트리를 타고 내려가며 모든 users 배열 합치기
    const recurseAgents = (agentNode) => {
      // 해당 에이전트의 직속 users
      if (agentNode.users) {
        agentNode.users.forEach((u) => list.push(u));
      }
      // 그 에이전트의 하위 에이전트들
      if (agentNode.agents) {
        agentNode.agents.forEach((childAgent) => recurseAgents(childAgent));
      }
    };

    // 최상위 agents 배열부터 시작
    users.agents.forEach((agent) => recurseAgents(agent));
    return list;
  };
  const allUsers = getAllUsers();

  const fmtDate = (ms) => dayjs(ms).tz("Asia/Seoul").format("YYYY-MM-DD");
  const fmtTime = (ms) =>
    dayjs(ms).tz("Asia/Seoul").format("HH:mm:ss:SSS") + " UTC +9";

  useEffect(() => {
    if (!users) return;
    fetchLogsData();
  }, [users]);

  const renderRow = (log, idx) => {
    if (!totalLogs || totalLogs.length === 0) {
      return (
        <tr>
          <td className="text-center span-cell-emt" colSpan="14">
            <div className="nk-order-ovwg-ck text-red">
              <h5>조회된 정보가 없습니다.</h5>
            </div>
          </td>
        </tr>
      );
    }

    const user = getAllUsers().find((u) => u.userId === log.userId) || {};
    const agentId = user.agent || "-";
    const isBet = log.target === "vendor_bet" || log.target === "game_bet";
    const isResult =
      log.target === "vendor_result" || log.target === "game_result";

    return (
      <tr key={log.idx} data-pos-y="0">
        {/* siteId 부분 */}
        <td
          className=""
          colSpan="1"
          data-columns="gameList-col-3"
          data-org-colspan="1"
          data-pos-x="3"
          data-priority="1"
          style={{ display: "table-cell" }}
        >
          <div className="user-toggle">
            <div className="user-toggle">
              <div className="user-info">
                <span className="badge rounded-pill badge-dim bg-outline-info me-1">
                  지갑
                </span>
                <em className="badge rounded-pill badge-dim bg-outline-info me-1 simm">
                  S
                </em>
                <div className="user-name">
                  <span className="badge rounded-pill badge-dim bg-outline-danger me-1">
                    아이디
                  </span>{" "}
                  {log.user_id || "-"}
                </div>
              </div>
            </div>
          </div>
        </td>

        {/* 화폐 */}
        <td
          className="text-center"
          colSpan="1"
          data-columns="gameList-col-4"
          data-org-colspan="1"
          data-pos-x="4"
          data-priority="1"
          style={{ display: "table-cell" }}
        >
          {vendorMap[log.vendor] ?? log.vendor}
        </td>

        {/* 액션(method) */}
        <td
          className="text-center"
          colSpan="1"
          data-columns="gameList-col-5"
          data-org-colspan="1"
          data-pos-x="5"
          data-priority="1"
          style={{ display: "table-cell" }}
        >
          <span
            style={{
              color: isBet ? "#dc3545" : isResult ? "#0d6efd" : "#6c757d",
              fontWeight: 600,
            }}
          >
            {log.target === "vendor_result"
              ? "밴더 당첨한도 변경"
              : log.target === "vendor_bet"
                ? "밴더 베팅한도 변경"
                : log.target === "game_bet"
                  ? "게임 베팅한도 변경"
                  : log.target === "game_result"
                    ? "게임 당첨한도 변경"
                    : "-"}
          </span>
        </td>

        <td
          className="text-center"
          colSpan="1"
          data-columns="gameList-col-6"
          data-org-colspan="1"
          data-pos-x="6"
          data-priority="1"
          style={{ display: "table-cell" }}
        >
          {log.target === "vendor_result"
            ?  vendorMap[log.vendor]+" 밴더 당첨한도 변경" ?? log.vendor 
            : log.target === "vendor_bet"
              ? log.target_id+"레벨 변경"
              : log.target === "game_bet" || log.target ==="game_result"
                 ? getGameName(log, GameList)
    : log.target_id}

        </td>

        <td
          className="text-center"
          colSpan="1"
          data-columns="gameList-col-7"
          data-org-colspan="1"
          data-pos-x="7"
          data-priority="1"
          style={{ display: "table-cell" }}
        >
          {(log.before_amount || 0).toLocaleString()}
        </td>

        <td
          className="text-center"
          colSpan="1"
          data-columns="gameList-col-8"
          data-org-colspan="1"
          data-pos-x="8"
          data-priority="1"
          style={{ display: "table-cell" }}
        >
          {(log.new_amount || 0).toLocaleString()}
        </td>
        <td
          className="text-center"
          colSpan="1"
          data-columns="gameList-col-9"
          data-org-colspan="1"
          data-pos-x="9"
          data-priority="1"
          style={{ display: "table-cell" }}
        >
          {log.ip}
        </td>

        {/* 요청시간 */}
        <td
          className="text-center"
          colSpan="1"
          data-columns="gameList-col-10"
          data-org-colspan="1"
          data-pos-x="10"
          data-priority="1"
          style={{ display: "table-cell" }}
        >
         {log.update_time
  ?.replace("T", " ")
  ?.replace(".000Z", "")}
        </td>
        {/* 응답시간 */}
      </tr>
    );
  };

  return (
    <div className="nk-content">
      <div className="container-fluid">
        <div className="nk-content-inner">
          <div className="nk-content-body">
            <div className="nk-block d-flex vLogsAgent">
              <div className="w-100 card card-bordered">
                <div className="card-inner-group">
                  <div className="card-inner p-3 p-0">
                    <form
                      className="mb-0"
                      onSubmit={(e) => {
                        e.preventDefault();
                        fetchLogsData();
                      }}
                    >
                      <DateTimeRangePicker
                        startDateTime={startDateTime}
                        endDateTime={endDateTime}
                        onStartChange={setStartDateTime}
                        onEndChange={setEndDateTime}
                        onPresetChange={handleDateRange}
                      >
                        <div className="col-12 col-sm-6 col-lg-6 col-xl-3 col-xxl-4">
                          <div className="form-control-wrap">
                            <div
                              className="input-group"
                              style={{
                                flexWrap: "nowrap",
                              }}
                            >
                              <div className="input-group-prepend">
                                <span className="btn btn-outline-light btn-dim">
                                  아이디
                                </span>
                              </div>

                              <input
                                type="text"
                                className="form-control"
                                placeholder="검색어"
                                value={searchKeyword}
                                onChange={(e) =>
                                  setSearchKeyword(e.target.value)
                                }
                              />

                              <div className="input-group-append">
                                <button
                                  type="submit"
                                  className="btn btn-outline-primary btn-dim"
                                >
                                  <em className="icon ni ni-search me-2" />
                                  검색
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </DateTimeRangePicker>
                    </form>
                  </div>

                  <div className="card-inner tableBox px-0 pb-0">
                    <div className="table-wrapper">
                      <div className="table-responsive">
                        <div data-pattern="priority-columns">
                          <table
                            cellSpacing="0"
                            className="table table-small-font table-tighten table-bordered table-striped"
                          >
                            <thead>
                              <tr data-pos-y="0">
                                <th
                                  className="text-center"
                                  data-pos-x="3"
                                  id="gameList-col-3"
                                >
                                  에이전트 정보
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="4"
                                  id="gameList-col-4"
                                >
                                  벤더
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="5"
                                  id="gameList-col-5"
                                >
                                  타겟
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="6"
                                  id="gameList-col-6"
                                >
                                  타겟 아이디
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="7"
                                  id="gameList-col-7"
                                >
                                  변경전 금액
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="8"
                                  id="gameList-col-8"
                                >
                                  변경후 금액
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="9"
                                  id="gameList-col-9"
                                >
                                  변경한 IP
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="10"
                                  id="gameList-col-9"
                                >
                                  요청시간
                                </th>
                              </tr>
                            </thead>

                            {totalLogs && (
                              <PaginatedSortableTable
                                data={totalLogs}
                                renderRow={renderRow}
                                defaultSortField="time_request"
                                defaultSortOrder="desc"
                                sortOptions={sortOptions}
                              />
                            )}
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* <div className="card-inner">
                    <div className="row justify-end">
                      <div className="col-12 col-md-4 col-xxl-4">
                        <ul
                          className="pagination justify-content-center justify-content-md-start"
                          style={{
                            float: "right",
                          }}
                        >
                          <li className="page-item">
                            <a className="page-link disabled" href="#">
                              <i className="fas fa-angle-double-left" />
                              <font>처음</font>
                            </a>
                          </li>
                          <li className="page-item">
                            <a className="page-link disabled" href="#">
                              <i className="fa fa-angle-left" />
                              <font>이전</font>
                            </a>
                          </li>
                          <li className="page-item">
                            <input
                              className="form-control text-center"
                              type="number"
                            />
                          </li>
                          <li className="page-item">
                            <a className="page-link" href="#">
                              <font>다음</font>
                              <i className="fa fa-angle-right" />
                            </a>
                          </li>
                        </ul>
                      </div>
                      <div className="col-6 col-md-2 col-xxl-2">
                        <select className="form-select">
                          <option value="10">리스트 - 10</option>
                          <option value="20">리스트 - 20</option>
                          <option value="50">리스트 - 50</option>
                          <option value="100">리스트 - 100</option>
                        </select>
                      </div>
                      <div className="col-6 col-md-2 col-xxl-2">
                        <select className="form-select">
                          <option value="-1">처리시간 - 내림차순</option>
                          <option value="1">처리시간 - 오름차순</option>
                        </select>
                      </div>
                    </div>
                  </div> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
